import { useState } from "react";
import { Copy, Eye, RefreshCw, Share } from "lucide-react";
import { AcDialog, Icon } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { useRotateVenueToken, useVenueToken, type LookId } from "../../app/hooks/useProduction.js";
import { absolutePublicUrl } from "./advanceLink.js";
import styles from "./AdvanceLookSwitcher.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/**
 * The venue-link actions in the Advance Docs header (PO, ART-251) — beside the look switcher, acting on THIS look's
 * link. They replace the "Advance Docs Link" card on every shell.
 *
 *  - **Mobile** — two pill buttons, **Share** (the native share sheet; the clipboard where there is none) and
 *    **Copy**. No Rotate: rotation is the destructive half and a phone that sends links never needs it.
 *  - **Desktop** — one compact bar, `LINK  /venue/…  Copy · Preview · Rotate`. It shows the PATH, not the full URL
 *    (the origin is the same on every link and only costs width); Copy and Preview use the absolute URL. Rotate
 *    is confirmed behind a danger dialog — a control beside Copy is one mis-aimed press from killing a live URL.
 *
 * A look with no link yet shows a single **Generate link** button on either shell.
 */
export function AdvanceLinkPill({ lookId }: { lookId: LookId }) {
  const isDesktop = !usePhoneLayout();
  const q = useVenueToken(lookId);
  const rotate = useRotateVenueToken(lookId);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const path = q.data?.hasToken ? q.data.publicPath ?? undefined : undefined;
  const url = absolutePublicUrl(path);

  const copy = async (u: string) => {
    try { await navigator.clipboard.writeText(u); pushToast("Advance docs link copied.", "success"); }
    catch { pushToast("Couldn't copy the link.", "error"); }
  };

  const share = async (u: string) => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try { await navigator.share({ title: "Venue advance", url: u }); return; }
      // A user-cancelled sheet is silent; anything else degrades to the clipboard so the tap always answers.
      catch (err) { if (err instanceof DOMException && err.name === "AbortError") return; }
    }
    void copy(u);
  };

  const mint = (isFirst: boolean) =>
    rotate.mutate(undefined, {
      onSuccess: () => pushToast(isFirst ? "Advance docs link created." : "Advance docs link rotated — old links no longer work.", "success"),
      onError: (e) => pushToast(errorMessage(e, "Couldn't update the advance docs link."), "error"),
    });

  if (!q.data) return null;   // nothing to act on until the token read lands — no placeholder button

  if (!url || !path) {
    return (
      <button type="button" className={styles.trigger} disabled={rotate.isPending} onClick={() => mint(true)}>
        <span className={styles.triggerValue}>{rotate.isPending ? "Creating…" : "Generate link"}</span>
      </button>
    );
  }

  if (!isDesktop) {
    return (
      <div className={styles.linkActions}>
        <button type="button" className={styles.trigger} aria-label="Share advance docs link" onClick={() => void share(url)}>
          <Icon icon={Share} size={16} />
          <span className={styles.triggerValue}>Share</span>
        </button>
        <button type="button" className={styles.trigger} aria-label="Copy advance docs link" onClick={() => void copy(url)}>
          <Icon icon={Copy} size={16} />
          <span className={styles.triggerValue}>Copy</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.linkBar}>
      <span className={styles.triggerKey}>Link</span>
      <span className={styles.linkPath} title={url}>{path}</span>
      <button type="button" className={styles.linkBtn} aria-label="Copy advance docs link" onClick={() => void copy(url)}>
        <Icon icon={Copy} size={16} />Copy
      </button>
      <button type="button" className={styles.linkBtn} aria-label="Preview advance docs link"
        onClick={() => window.open(url, "_blank", "noopener")}>
        <Icon icon={Eye} size={16} />Preview
      </button>
      <button type="button" className={styles.linkBtn} aria-label="Rotate advance docs link"
        disabled={rotate.isPending} onClick={() => setConfirmRotate(true)}>
        <Icon icon={RefreshCw} size={16} />{rotate.isPending ? "Rotating…" : "Rotate"}
      </button>

      {confirmRotate && (
        <AcDialog
          title="Rotate this advance docs link?"
          message="The current url stops working the moment the new one is minted — every venue you have already sent it to gets a 404, and there is no way back to the old url."
          confirmLabel="Rotate link"
          tone="danger"
          loading={rotate.isPending}
          onCancel={() => setConfirmRotate(false)}
          onConfirm={() => { setConfirmRotate(false); mint(false); }}
        />
      )}
    </div>
  );
}
